(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsObserver = 'IntersectionObserver' in window;
  const revealClass = 'scroll-reveal';
  const visibleClass = 'is-visible';
  const revealQueue = new WeakSet();
  const scanSelector = [
    '#MainContent .section-content-wrapper > *',
    '#MainContent .resource-list__item',
    '#MainContent .product-card',
    '#MainContent .collection-card',
    '#MainContent .featured-blog-posts-card',
    '#MainContent .product-media-container',
    '#MainContent .collection-info__content > *',
    '#MainContent .rte > :where(h1, h2, h3, h4, h5, h6, p, ul, ol, blockquote, table)',
    '#MainContent [class*="__content"] > :where(h1, h2, h3, h4, h5, h6, p, ul, ol, blockquote, .button, [class*="button"])',
    '#MainContent [class$="__media"]',
    '#MainContent [class*="__visual"]',
    '#MainContent [class$="__card"]',
    '#MainContent .site-check__group',
    '#MainContent .product-information :where(product-price, variant-picker, product-form, accordion-custom, details, .product-inventory, .text-block, .button)',
  ].join(',');
  const skipSelector = [
    'script',
    'style',
    'noscript',
    'template',
    '[hidden]',
    '[aria-hidden="true"]',
    'dialog',
    '.shopify-section:empty',
    '.scroll-reveal-skip',
    '.quick-add-modal',
    '.search-modal',
    '.slideshow-controls',
    '.slideshow-arrows',
    '.product-media-container__zoom-button',
    '.deferred-media__poster-button',
    '.close-button',
    '.button-unstyled',
  ].join(',');
  const cardSelector = [
    '.product-card',
    '.collection-card',
    '.resource-list__item',
    '.featured-blog-posts-card',
    '.predictive-search-results__card',
    '[class$="__card"]',
  ].join(',');
  const headingSelector = ':where(h1, h2, h3, .text-block, .section-title, [class*="__heading"])';
  const mediaSelector = '.product-media-container, [class$="__media"], [class*="__visual"], picture, img';
  const groupSelector = [
    '#MainContent .shopify-section',
    '#MainContent .section-content-wrapper',
    '#MainContent .resource-list',
    '#MainContent .product-grid',
    '#MainContent .collection-wrapper',
    '#MainContent .product-information',
    '#MainContent .home-hero-benefits',
    '#MainContent .home-screw-packers',
    '#MainContent .home-daypacker',
    '#MainContent .home-custom-fit',
    '#MainContent .home-packer-proof',
    '#MainContent .home-site-consult',
    '#MainContent .home-custom-faq',
    '#MainContent .site-check',
    '#MainContent .accessories-hero',
    '#MainContent .accessories-conversion',
  ].join(',');
  let observer;

  function shouldSkip(element) {
    if (!(element instanceof HTMLElement)) return true;
    if (element.matches(skipSelector) || element.closest(skipSelector)) return true;
    if (!element.getClientRects().length) return true;

    const card = element.closest(cardSelector);
    if (card && card !== element) return true;

    if (element.closest('footer, #header-group, .menu-drawer, cart-drawer, quick-add-modal, predictive-search')) {
      return true;
    }

    return false;
  }

  function setVariant(element) {
    if (element.matches(headingSelector)) {
      element.classList.add('scroll-reveal--heading');
    }

    if (element.closest('.hero, .home-hero-benefits, .accessories-hero') || element.getBoundingClientRect().top < window.innerHeight * 0.45) {
      element.classList.add('scroll-reveal--hero');
      return;
    }

    if (element.matches(mediaSelector)) {
      element.classList.add('scroll-reveal--image');
      return;
    }

    if (element.matches(cardSelector)) {
      element.classList.add('scroll-reveal--card');
      return;
    }

    const row = element.closest('.layout-panel-flex--row, .media-with-content, .home-custom-fit, .home-site-consult, .home-daypacker');
    if (row) {
      const siblings = Array.from(element.parentElement?.children || []).filter((child) => child instanceof HTMLElement);
      const index = siblings.indexOf(element);

      if (index === 0) {
        element.classList.add('scroll-reveal--from-left');
      } else if (index === 1) {
        element.classList.add('scroll-reveal--from-right');
      }
    }
  }

  function setDelay(element) {
    const group = element.closest(groupSelector);

    if (!group) return;

    const items = Array.from(group.querySelectorAll(`.${revealClass}`)).filter((item) => item.closest(groupSelector) === group);
    const index = items.indexOf(element);

    if (index < 1) return;

    const rect = element.getBoundingClientRect();
    const isHeroVisible = rect.top < window.innerHeight * 0.75;
    const delay = Math.min(index * 125, isHeroVisible ? 240 : 520);

    element.style.setProperty('--scroll-reveal-delay', `${delay}ms`);
  }

  function reveal(element) {
    element.classList.add(visibleClass);
    observer?.unobserve(element);
  }

  function scheduleReveal(element) {
    if (revealQueue.has(element)) return;

    revealQueue.add(element);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => reveal(element));
    });
  }

  function getObserver() {
    if (!supportsObserver) return undefined;
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) scheduleReveal(entry.target);
        }
      },
      {
        rootMargin: '0px 0px -14% 0px',
        threshold: 0.01,
      }
    );

    return observer;
  }

  function collect(scope = document) {
    return Array.from(scope.querySelectorAll(scanSelector)).filter((element, index, elements) => {
      return elements.indexOf(element) === index && !element.classList.contains(revealClass) && !shouldSkip(element);
    });
  }

  function init(scope = document) {
    if (reducedMotion.matches) return;

    const elements = collect(scope);
    if (!elements.length) return;

    root.classList.add('scroll-reveal-enabled');

    for (const element of elements) {
      element.classList.add(revealClass);
      setVariant(element);
      setDelay(element);
    }

    const activeObserver = getObserver();

    for (const element of elements) {
      const rect = element.getBoundingClientRect();

      if (!activeObserver || (rect.top < window.innerHeight * 0.92 && rect.bottom > 0)) {
        scheduleReveal(element);
      } else {
        activeObserver.observe(element);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
